import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, toastError } from "../../lib/api";
import { PageHeader } from "../../components/domain/Bits";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { ArrowLeft, UserMinus } from "lucide-react";
import { toast } from "sonner";

type Item = { key: string; work_type: string; item_id: string; title: string; status: string; due_at?: string | null };
type Replacement = { id: string; name: string; email: string };
type Preview = {
  user: { id: string; name: string; email: string };
  counts: Record<string, number>; total: number; items: Item[];
  eligible_replacements: Replacement[];
};

export default function StaffOffboarding() {
  const { userId = "" } = useParams();
  const navigate = useNavigate();
  const [preview, setPreview] = useState<Preview | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [assignments, setAssignments] = useState<Record<string, string>>({});
  const [filter, setFilter] = useState("all");
  const [bulkUser, setBulkUser] = useState("");
  const [reason, setReason] = useState("");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [acknowledge, setAcknowledge] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get(`/tenant/users/${userId}/pending-work`)
      .then(({ data }) => setPreview(data))
      .catch(toastError);
  }, [userId]);

  const visible = useMemo(
    () => (preview?.items || []).filter((item) => filter === "all" || item.work_type === filter),
    [preview, filter],
  );
  const unassigned = (preview?.items || []).filter((item) => !assignments[item.key]);
  const toggle = (key: string) => setSelected((old) => {
    const next = new Set(old); next.has(key) ? next.delete(key) : next.add(key); return next;
  });
  const applyBulk = () => {
    if (!bulkUser) return;
    setAssignments((old) => {
      const next = { ...old }; selected.forEach((key) => { next[key] = bulkUser; }); return next;
    });
    setSelected(new Set());
  };
  const submit = async () => {
    if (!preview || reason.trim().length < 3) return;
    if (unassigned.length && !acknowledge) {
      toast.error("Resolve every pending item or acknowledge the retained items.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post(`/tenant/users/${userId}/offboard`, {
        resignation_date: date, reason: reason.trim(),
        transfers: preview.items.filter((item) => assignments[item.key]).map((item) => ({
          work_type: item.work_type, item_id: item.item_id,
          replacement_user_id: assignments[item.key],
        })),
        acknowledged_unassigned: unassigned.map((item) => item.key),
      });
      toast.success(`${preview.user.name} was deactivated safely`);
      navigate("/admin/users");
    } catch (error) { toastError(error); } finally { setSubmitting(false); }
  };

  if (!preview) return <div className="p-8 text-sm text-slate-500">Loading pending work…</div>;
  const types = Object.keys(preview.counts);
  return <>
    <PageHeader title={`Offboard ${preview.user.name}`} subtitle="Transfer unfinished responsibility before removing access"
      actions={<Button variant="outline" asChild><Link to="/admin/users"><ArrowLeft className="w-4 h-4 mr-1" /> Users</Link></Button>} />
    <div className="max-w-7xl mx-auto p-6 md:p-8 space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        <div className="rounded-lg border bg-white p-4"><div className="text-xs text-slate-500">Total actionable</div><div className="text-2xl font-semibold">{preview.total}</div></div>
        {types.map((type) => <div key={type} className="rounded-lg border bg-white p-4"><div className="text-xs capitalize text-slate-500">{type}</div><div className="text-2xl font-semibold">{preview.counts[type]}</div></div>)}
      </div>

      <div className="rounded-lg border bg-white p-4 space-y-4">
        <div className="flex flex-wrap gap-3 items-end">
          <div><Label>Work type</Label><Select value={filter} onValueChange={setFilter}><SelectTrigger className="w-44"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="all">All actionable work</SelectItem>{types.map((type) => <SelectItem key={type} value={type}>{type}</SelectItem>)}</SelectContent></Select></div>
          <div><Label>Bulk replacement</Label><Select value={bulkUser} onValueChange={setBulkUser}><SelectTrigger className="w-64"><SelectValue placeholder="Choose active user" /></SelectTrigger><SelectContent>{preview.eligible_replacements.map((user) => <SelectItem key={user.id} value={user.id}>{user.name} · {user.email}</SelectItem>)}</SelectContent></Select></div>
          <Button variant="outline" onClick={applyBulk} disabled={!bulkUser || !selected.size}>Assign {selected.size || ""} selected</Button>
          <Button variant="ghost" onClick={() => setSelected(new Set(visible.map((item) => item.key)))}>Select visible</Button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="pending-work-table">
            <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500"><tr><th className="p-3">Select</th><th className="p-3">Type</th><th className="p-3">Item</th><th className="p-3">Status / due</th><th className="p-3">Transfer to</th></tr></thead>
            <tbody>{visible.map((item) => <tr key={item.key} className="border-t">
              <td className="p-3"><input type="checkbox" checked={selected.has(item.key)} onChange={() => toggle(item.key)} aria-label={`Select ${item.title}`} /></td>
              <td className="p-3 capitalize">{item.work_type}</td><td className="p-3 font-medium">{item.title}</td>
              <td className="p-3 text-slate-600">{item.status}{item.due_at ? ` · ${String(item.due_at).slice(0, 10)}` : ""}</td>
              <td className="p-3"><Select value={assignments[item.key] || "__retain__"} onValueChange={(value) => setAssignments((old) => {
                const next = { ...old }; value === "__retain__" ? delete next[item.key] : next[item.key] = value; return next;
              })}><SelectTrigger className="w-64"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="__retain__">Retain unassigned</SelectItem>{preview.eligible_replacements.map((user) => <SelectItem key={user.id} value={user.id}>{user.name}</SelectItem>)}</SelectContent></Select></td>
            </tr>)}</tbody>
          </table>
        </div>
      </div>

      <div className="rounded-lg border border-rose-200 bg-rose-50 p-5 space-y-4">
        <div><h2 className="font-semibold text-rose-900">Final reconciliation</h2><p className="text-sm text-rose-800">{Object.keys(assignments).length} transferring · {unassigned.length} intentionally retained. Historical authorship, commissions, expenses, and completed records remain unchanged.</p></div>
        <div className="grid md:grid-cols-2 gap-3"><div><Label>Resignation / final date</Label><Input type="date" value={date} onChange={(e) => setDate(e.target.value)} /></div><div><Label>Reason</Label><Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Resigned, contract ended, role change…" /></div></div>
        {!!unassigned.length && <label className="flex gap-2 text-sm text-rose-900"><input type="checkbox" checked={acknowledge} onChange={(e) => setAcknowledge(e.target.checked)} /> I acknowledge that {unassigned.length} actionable item(s) will remain unassigned and have reviewed the operational risk.</label>}
        <Button variant="destructive" onClick={submit} disabled={submitting || reason.trim().length < 3 || (!!unassigned.length && !acknowledge)}><UserMinus className="w-4 h-4 mr-1" /> {submitting ? "Deactivating…" : "Transfer work and deactivate"}</Button>
      </div>
    </div>
  </>;
}
